import React, { useState, useEffect } from 'react'
import { Card, CardBody, Row, Col, FormGroup, Label, Input, Button, Alert } from 'reactstrap'
import { useNavigate, useLocation } from 'react-router-dom'
import { addData } from '../../lib/taskm'
import { getInstructorStudent } from '../../lib/instructorstudentview'
import { getInstructorIdData } from '../../lib/classview'
import { getDataWithClass } from '../../lib/classdetailview'
import { useAuth } from '../../_helper/Auth/AuthProvider'
import { TextEditor } from '../Common/TextEditor'
import { TaskTopic911Select, TaskTopic12Select } from '../Common/FormSelects'
import { getCurrentDateInput } from '../../utils/dateTimeUtils'
import { useLayout } from '../../Hooks/useLayout'
import { useRoleBasedUrl } from '../../Hooks/useRoleBasedUrl'

const InstructorTaskAdd = () => {
	const [selectedStudent, setSelectedStudent] = useState('All')
	const [selectedClass, setSelectedClass] = useState('')
	const [desc, setDesc] = useState('')
	const [category, setCategory] = useState('Student')
	const [topicType, setTopicType] = useState('Grade911')
	const [instructorStudent, setInstructorStudent] = useState([])
	const [instructorClass, setInstructorClass] = useState([])
	const [classDetail, setClassDetail] = useState([])
	const [taskTopic, setTaskTopic] = useState('')
	const [date, setDate] = useState('')
	const [error, setError] = useState('')
	const [loading, setLoading] = useState(false)
	const [initialLoading, setInitialLoading] = useState(true)
	const [isDarkMode, setIsDarkMode] = useState(false)

	const { user } = useAuth()
	const navigate = useNavigate()
	const location = useLocation()
	const layout = useLayout()
	const role = useRoleBasedUrl()

	// Get studentId from query parameters
	const queryParams = new URLSearchParams(location.search)
	const studentIdFromQuery = queryParams.get('studentId')

	const GetInstructorStudentData = async (id) => {
		try {
			setInitialLoading(true)
			const students = await getInstructorStudent(id)
			setInstructorStudent(students)

			// Pre-select student if studentId is provided in query
			if (studentIdFromQuery) {
				setSelectedStudent(studentIdFromQuery)
			}

			const clas = await getInstructorIdData(id)
			setInstructorClass(clas)
			if (clas.length > 0) {
				setSelectedClass(clas[0].id)
			}
		} catch (error) {
			console.error('Error fetching instructor data:', error)
			setError('Failed to load instructor data')
		} finally {
			setInitialLoading(false)
		}
	}

	const GetClassDetail = async () => {
		if (selectedClass && user?.id) {
			try {
				const classinfo = await getDataWithClass(selectedClass, user.id)
				setClassDetail(classinfo)
			} catch (error) {
				console.error('Error fetching class detail:', error)
				setError('Failed to load class details')
			}
		}
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setLoading(true)
		setError('')

		try {
			if (category === 'Student') {
				if (selectedStudent === 'All') {
					await Promise.all(
						instructorStudent.map(async (data) => {
							await addData({
								from: data.instructorId,
								to: data.studentId,
								subject: taskTopic,
								setDate: date,
								desc: desc
							})
						})
					)
				} else {
					await addData({
						from: user.id,
						to: selectedStudent,
						subject: taskTopic,
						setDate: date,
						desc: desc
					})
				}
			} else {
				await Promise.all(
					classDetail.map(async (data) => {
						await addData({
							from: data.instructorId,
							to: data.studentId,
							subject: taskTopic,
							desc: desc
						})
					})
				)
			}

			// Navigate back to student task page if studentId was provided, otherwise go to general tasks page
			if (studentIdFromQuery) {
				navigate(`${process.env.PUBLIC_URL}/${role}/student-tasks/${studentIdFromQuery}/${layout}`)
			} else {
				navigate(`${process.env.PUBLIC_URL}/${role}/tasks/${layout}`)
			}
		} catch (error) {
			console.error('Error adding task:', error)
			setError('Failed to add task')
		} finally {
			setLoading(false)
		}
	}

	const handleCancel = () => {
		// Navigate back to student task page if studentId was provided, otherwise go to general tasks page
		if (studentIdFromQuery) {
			navigate(`${process.env.PUBLIC_URL}/${role}/student-tasks/${studentIdFromQuery}/${layout}`)
		} else {
			navigate(`${process.env.PUBLIC_URL}/${role}/tasks/${layout}`)
		}
	}

	useEffect(() => {
		setDate(getCurrentDateInput())
		if (user?.id) {
			GetInstructorStudentData(user.id)
		}
	}, [user?.id])

	useEffect(() => {
		GetClassDetail()
	}, [selectedClass, user?.id])

	useEffect(() => {
		const checkDarkMode = () => {
			setIsDarkMode(document.body.classList.contains('dark-only'))
		}
		checkDarkMode()
		const observer = new MutationObserver(checkDarkMode)
		observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
		return () => observer.disconnect()
	}, [])

	useEffect(() => {
		if (isDarkMode) {
			const style = document.createElement('style')
			style.id = 'instructor-task-add-ckeditor-dark'
			style.textContent = `
				#editor .ck-editor__editable,
				#editor .ck-content,
				#editor .ck-content p,
				#editor .ck-content h1,
				#editor .ck-content h2,
				#editor .ck-content h3,
				#editor .ck-content h4,
				#editor .ck-content h5,
				#editor .ck-content h6,
				#editor .ck-content li,
				#editor .ck-content span,
				#editor .ck-content div,
				#editor .ck-content strong,
				#editor .ck-content em,
				#editor .ck-content ul,
				#editor .ck-content ol {
					color: #fff !important;
				}
				#editor .ck-editor__editable {
					background-color: #2a2d35 !important;
				}
			`
			document.head.appendChild(style)
			return () => {
				const existingStyle = document.getElementById('instructor-task-add-ckeditor-dark')
				if (existingStyle) {
					existingStyle.remove()
				}
			}
		} else {
			const existingStyle = document.getElementById('instructor-task-add-ckeditor-dark')
			if (existingStyle) {
				existingStyle.remove()
			}
		}
	}, [isDarkMode])

	if (initialLoading) {
		return (
			<Card style={{ backgroundColor: isDarkMode ? '#2a2d35' : '#fff' }}>
				<CardBody style={{ backgroundColor: isDarkMode ? '#2a2d35' : '#fff' }}>
					<div className="text-center">
						<div className="spinner-border" role="status">
							<span className="visually-hidden">Loading...</span>
						</div>
						<p className="mt-2" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>Loading task data...</p>
					</div>
				</CardBody>
			</Card>
		)
	}

	return (
		<Card style={{ backgroundColor: isDarkMode ? '#2a2d35' : '#fff' }}>
			<CardBody style={{ backgroundColor: isDarkMode ? '#2a2d35' : '#fff' }}>
				<div className="d-flex justify-content-between align-items-center mb-4">
					<h3 style={{ color: isDarkMode ? '#fff' : 'inherit' }}>New Item</h3>
				</div>

				{error && (
					<Alert color="danger" className="mb-4">
						{error}
					</Alert>
				)}

				<form onSubmit={handleSubmit}>
					<Row>
						<Col md="12">
							<FormGroup>
								<Label className="form-control-label" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>Category</Label>
								<div className="d-flex gap-4">
									<div className="form-check">
										<Input
											className="form-check-input"
											type="radio"
											name="category"
											id="flexRadioDefault1"
											value="Student"
											checked={category === 'Student'}
											onChange={(e) => setCategory('Student')}
											disabled={loading}
										/>
										<Label className="form-check-label" htmlFor="flexRadioDefault1" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>
											Student
										</Label>
									</div>
									<div className="form-check">
										<Input
											className="form-check-input"
											type="radio"
											name="category"
											id="flexRadioDefault2"
											value="Class"
											checked={category === 'Class'}
											onChange={(e) => setCategory('Class')}
											disabled={loading}
										/>
										<Label className="form-check-label" htmlFor="flexRadioDefault2" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>
											Class
										</Label>
									</div>
								</div>
							</FormGroup>
						</Col>
					</Row>

					<Row>
						<Col md="6">
							<FormGroup>
								<Label className="form-control-label" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>{category === 'Student' ? 'Students' : 'Class'}</Label>
								<Input
									type="select"
									value={category === 'Student' ? selectedStudent : selectedClass}
									onChange={(e) => {
										if (category === 'Student') {
											setSelectedStudent(e.target.value)
										} else {
											setSelectedClass(e.target.value)
										}
									}}
									disabled={loading}
									required
									style={{
										backgroundColor: isDarkMode ? '#2a2d35' : '#fff',
										color: isDarkMode ? '#fff' : 'inherit'
									}}
								>
									{category === 'Student' ? (
										<>
											<option value="All">All</option>
											{instructorStudent.map((data, ix) => (
												<option key={ix} value={data.studentId}>
													{data.name + ' ' + data.surname}
												</option>
											))}
										</>
									) : (
										instructorClass.map((data, ix) => (
											<option key={ix} value={data.id}>
												{data.class_name}
											</option>
										))
									)}
								</Input>
							</FormGroup>
						</Col>
						<Col md="6">
							<FormGroup>
								<Label className="form-control-label" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>Date</Label>
								<Input
									type="date"
									value={date}
									onChange={(e) => setDate(e.target.value)}
									disabled={loading}
									required
									style={{
										backgroundColor: isDarkMode ? '#2a2d35' : '#fff',
										color: isDarkMode ? '#fff' : 'inherit'
									}}
								/>
							</FormGroup>
						</Col>
					</Row>

					<Row>
						<Col md="12">
							<FormGroup>
								<Label className="form-control-label" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>Topic Type</Label>
								<div className="d-flex gap-4 mb-3">
									<div className="form-check">
										<Input
											className="form-check-input"
											type="radio"
											name="topicType"
											id="topicType1"
											value="Grade911"
											checked={topicType === 'Grade911'}
											onChange={(e) => setTopicType('Grade911')}
											disabled={loading}
										/>
										<Label className="form-check-label" htmlFor="topicType1" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>
											Grade 9-11
										</Label>
									</div>
									<div className="form-check">
										<Input
											className="form-check-input"
											type="radio"
											name="topicType"
											id="topicType2"
											value="Grade12"
											checked={topicType === 'Grade12'}
											onChange={(e) => setTopicType('Grade12')}
											disabled={loading}
										/>
										<Label className="form-check-label" htmlFor="topicType2" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>
											Grade 12
										</Label>
									</div>
								</div>
								{topicType === 'Grade911' ? (
									<TaskTopic911Select value={taskTopic} setValue={setTaskTopic} isDarkMode={isDarkMode} />
								) : (
									<TaskTopic12Select value={taskTopic} setValue={setTaskTopic} isDarkMode={isDarkMode} />
								)}
							</FormGroup>
						</Col>
					</Row>

					<Row>
						<Col md="12">
							<FormGroup>
								<Label className="form-control-label" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>Description</Label>
								<div id="editor">
									<TextEditor value={desc} setValue={setDesc} />
								</div>
							</FormGroup>
						</Col>
					</Row>

					<div className="d-flex justify-content-between mt-4">
						<Button type="submit" color="primary" disabled={loading}>
							{loading ? 'Adding...' : 'Add Item'}
						</Button>
						<Button type="button" color="secondary" onClick={handleCancel} disabled={loading}>
							Cancel
						</Button>
					</div>
				</form>
			</CardBody>
		</Card>
	)
}

export { InstructorTaskAdd }
